<template>
  <div class="flex flex-col gap-6">
    <div class="bg-white shadow-md rounded-md p-4">
      <div class="flex justify-between">
        <div class="flex flex-col">
          <p class="font-bold">Template Upload</p>
          <p class="text-xs">Mohon gunakan template yang sudah disediakan untuk upload paket.</p>
        </div>
        <Button @action="downloadTemplate" :buttonText="$t('order.downloadTemplateOrder')" :type="'secondary'" :size="isMobile ? 'full' : 'regular'" />
      </div>
    </div>
    <div class="bg-white shadow-md rounded-md p-4 flex flex-col gap-4">
      <div class="mb-4 text-left relative">
        <label class="text-sm text-neutral-700 font-medium mb-1 inline-block">{{ $t('order.createOrder.stepOne.senderAddress') }}</label>
        <div class="relative">
          <TextField withIcon type="text" @input="searchAddress($event, 'senderAddress')" v-model="senderAddressSearch" :placeholder="$t('order.createOrder.stepOne.senderAddressPlaceholder')" />
          <div class="absolute left-3 top-1/2 transform -translate-y-1/2">
            <Search />
          </div>
          <img src="@/assets/images/rolling.gif" class="w-8 absolute top-1/2 transform -translate-y-1/2 right-2" v-if="isSearching['senderAddress']" />
          <transition name="fade" mode="out-in">
            <div class="mt-1 z-10 border rounded-lg absolute top-full left-0 w-full bg-white" v-if="visibleSuggestion['senderAddress']">
              <ul v-if="addresses" class="max-h-40 overflow-y-scroll shadow-base">
                <li class="cursor-pointer px-3 py-2 hover:bg-neutral-300 " v-for="(address, index) in addresses" :key="'address-' + index" @click="selectAddress(address, 'senderAddress')">
                  {{ address.location_label }}
                </li>
              </ul>
              <div class="py-4 text-center" v-else>
                {{ $t('order.createOrder.stepOne.dataNotFound') }}
              </div>
            </div>
          </transition>
        </div>
      </div>
      <!-- Upload Box -->
      <div class="w-full mx-auto">
        <label
          for="file-upload"
          class="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed rounded-xl cursor-pointer transition"
          :class="[senderAddressSearch ? 'border-gray-300 bg-gray-50 hover:bg-gray-100 cursor-pointer' : 'border-red-400 bg-gray-100 cursor-not-allowed opacity-50']"
        >
          <div class="flex flex-col items-center justify-center pt-5 pb-6">
            <svg v-if="!uploadedFile" class="w-12 h-12 mb-3 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
              <path d="M3 3a2 2 0 012-2h3a1 1 0 110 2H5v14h10V3h-3a1 1 0 110-2h3a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V3z" />
              <path
                d="M9 7a1 1 0 112 0v5.586l1.293-1.293a1 1 0 
             111.414 1.414l-3 3a1 
             1 0 01-1.414 0l-3-3a1 
             1 0 111.414-1.414L9 12.586V7z"
              />
            </svg>

            <!-- Show file name if uploaded -->
            <template v-if="uploadedFile">
              <p class="text-lg font-semibold text-gray-700">📂 {{ uploadedFile.name }}</p>
              <p class="text-xs text-gray-500 mt-1">Ukuran: {{ (uploadedFile.size / 1024).toFixed(2) }} KB</p>
            </template>

            <!-- Default display -->
            <template v-else>
              <p class="text-lg font-semibold text-gray-700">Unggah File Disini</p>
              <p class="text-sm text-gray-500">
                Letakkan file kamu di sini atau
                <span class="text-gray-600 font-medium underline">pilih file</span>
              </p>
              <p class="text-xs text-gray-400 mt-1">Format: XLSX, Ukuran Maksimum: 10 MB</p>
              <p v-if="!senderAddressSearch" class="text-xs text-red-500 mt-2">
                *Harap isi Alamat Pengirim terlebih dahulu
              </p>
            </template>
          </div>

          <!-- real input -->
          <input id="file-upload" type="file" class="hidden" ref="fileUpload" @change="onFileChange" :disabled="!senderAddressSearch" />
        </label>
      </div>

      <Button :buttonText="uploadIsLoading ? 'Uploading...' : 'Upload File'" type="primary" :disabled="!senderAddressSearch || uploadIsLoading" @action="submitFile" :isLoading="uploadIsLoading">
      </Button>
    </div>
    <div class="bg-white shadow-md" v-if="!uploadIsLoading">
      <div class="p-4 ">
        <Button buttonText="Create Order" type="primary" v-if="filteredData.length > 0" :disabled="hasErrors" @action="submitMultipleOrder" />
      </div>
      <div class="" v-if="filteredData && filteredData.length > 0">
        <div v-for="tempOrder in filteredData" :key="tempOrder.id">
          <UploadOrderCard :data="tempOrder" />
        </div>
      </div>
      <div class="py-10 text-center" v-else>{{ $t('order.dataNotFound') }}</div>
    </div>
  </div>
</template>

<script>
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'
import { mapActions, mapGetters } from 'vuex'
import { isNotEmptyError } from '@/util/form-validator'

export default {
  components: {
    TextField: () => import(/* webpackChunkName: "Forms" */ '@/components/Forms/TextField'),
    Search: () => import(/* webpackChunkName: "Icons" */ '@/components/Icons/Search'),
    Button: () => import(/* webpackChunkName: "Forms" */ '@/components/Forms/Button'),
    UploadOrderCard: () => import(/* webpackChunkName: "card" */ '@/components/OrderCard/UploadOrderCard')
  },
  data: () => ({
    isUsingExistingReceiverAddress: false,
    visibleAddressModal: false,
    visibleCustom: false,
    goodsWeight: 0,
    addressDetail: null,
    packageList: ['a', 'b'],
    selectedPackage: null,
    selectedAddress: null,
    searchKeyword: null,
    isSearchingPlace: false,
    selectedPackageSize: null,
    uploadIsLoading: false,
    uploadedFile: null,
    isFetching: false,
    payloads: {
      sender_name: '',
      sender_phone: '',
      sender_address: '',
      sender_location_code: ''
    },
    cityKeyword: null,
    visibleSuggestion: {
      origin: false,
      destination: false,
      senderAddress: false
    },
    isSearching: {
      origin: false,
      destination: false,
      senderAddress: false
    },
    senderAddressSearch: '',
    senderAddressKeyword: '',
    address: null,
    autocomplete: null,
    autocompleteService: null,
    placeService: null,
    sessionToken: null,
    suggestionList: [],
    localStorageAsync: {
      set: function(key, value) {
        return Promise.resolve().then(function() {
          localStorage.setItem(key, value)
        })
      },
      get: function(key) {
        return Promise.resolve().then(function() {
          return localStorage.getItem(key)
        })
      }
    },
    isPOD: false,
    queries: {
      page: 1,
      per_page: 10,
      search_keyword: '',
      start_date: '',
      end_date: '',
      status: 'ALL',
      sort: 'DESC'
    }
  }),
  computed: {
    ...mapGetters('master', ['cities', 'packageSizes', 'goodsCategories', 'pickupSlotTime']),
    ...mapGetters('merchant', ['addresses', 'isAddressModalVisible']),
    ...mapGetters('common', ['isMobile']),
    ...mapGetters('order', ['tempOrderList']),
    isStepAvailable() {
      return this.orderStep.indexOf(this.$route.params.step) > -1
    },
    filteredData() {
      let temp = this.tempOrderList || []
      temp = temp.filter(item => {
        return item.id !== ''
      })
      return temp
    },
    hasErrors() {
      return this.filteredData.some(item => item.errors && Object.keys(item.errors).length > 0)
    },
    start() {
      return (this.queries.page - 1) * this.queries.per_page + 1
    },
    end() {
      const lastOnPage = (this.queries.page - 1) * this.queries.per_page + this.queries.per_page
      return lastOnPage > this.totalRecords ? this.totalRecords : lastOnPage
    },
    tabList() {
      return [
        {
          label: this.$t('order.tab.all'),
          value: 'ALL'
        },
        {
          label: this.$t('order.tab.ongoing'),
          value: 'ONGOING'
        },
        {
          label: this.$t('order.tab.unpaid'),
          value: 'UNPAID'
        },
        {
          label: this.$t('order.tab.completed'),
          value: 'COMPLETED'
        },
        {
          label: this.$t('order.tab.complain'),
          value: 'COMPLAIN'
        }
      ]
    },
    defaultAddress() {
      return this.addresses.filter(item => item.is_main)[0]
    },
    isFirstStep() {
      return this.$route.params.step === 'shipping'
    },
    isSecondStep() {
      return this.$route.params.step === 'logistic'
    },
    isThirdStep() {
      return this.$route.params.step === 'confirmation'
    },
    isCustomDisabled() {
      return !this.validForm(this.custom)
    },
    isDisabled() {
      return (
        !this.selectedAddress ||
        !this.receiverName ||
        !this.receiverPhoneNumber ||
        !this.destinationSearch ||
        !this.addressDetail ||
        !this.selectedPackage ||
        !this.selectedPackageSize ||
        (this.isScheduleOrder && !this.scheduleDate) ||
        (this.isScheduleOrder && !this.scheduleTime)
      )
    }
  },
  watch: {
    $route(val) {
      if (Object.keys(val.query).length > 0) {
        this.getTempOrderList(this.queries)
      } else {
        this.$router.push({ path: '/order/step/upload', query: this.queries })
      }
    }
  },
  mounted() {
    this.hideLoading()
    // eslint-disable-next-line
    this.sessionToken = new google.maps.places.AutocompleteSessionToken()
    // eslint-disable-next-line
    this.autocompleteService = new google.maps.places.AutocompleteService()
    // eslint-disable-next-line
    this.placeService = new google.maps.places.PlacesService(document.createElement('div'))

    const data = JSON.parse(localStorage.getItem('order_shipping'))
    if (data) {
      this.selectedAddress = data.sender
      this.senderAddressSearch = data.sender.location_label
      this.isUsingExistingReceiverAddress = data.isUsingExistingReceiverAddress
      this.isPOD = data.is_require_pod
      if (data.isUsingExistingReceiverAddress) {
        this.receiverAddress = data.receiver.selectedAddress
      }
      this.receiverName = data.receiver?.name
      this.receiverPhoneNumber = data.receiver?.phone
      this.goodsWeight = data.receiver?.weight
      this.searchKeyword = data.receiver?.location?.name
      this.address = {
        name: data.receiver?.location?.name,
        latitude: data.receiver?.location?.latitude,
        longitude: data.receiver?.location?.longitude
      }
      this.addressDetail = data.receiver?.addressDetail
      this.originSearch = data.hub.origin ? `${data.hub?.origin?.village}, ${data.hub?.origin?.area_display}` : ''
      this.destinationSearch = data.hub.destination ? `${data.hub?.destination?.village}, ${data.hub?.destination?.area_display}` : ''
      this.payloads.origin_city = data.hub.origin
      this.payloads.destination_city = data.hub.destination
      this.selectedPackage = data.hub.goodsCategory
      this.selectedPackageSize = data.package
      if (this.selectedPackageSize.code === 'CUSTOM') {
        this.custom.weight = data.receiver?.weight
        const dim = this.selectedPackageSize.dimension.split('x')
        this.custom.length = dim[0]
        this.custom.width = dim[1]
        this.custom.height = dim[2]
      }
      if (data.is_advance_pickup) {
        this.isScheduleOrder = true
        this.scheduleDate = data.pickup_date
        this.scheduleTime = data.pickup_slot_id
      }
    }
  },
  created() {
    this.getPackageSizeList()
    this.getGoodsCategoryList()
    // this.getSenderAddress()
    this.getPickupSlotTimeList()
    if (Object.keys(this.$route.query).length < 1) {
      this.$router.push({ path: '/order/step/upload', query: this.queries })
      this.selectedTab = this.tabList.filter(item => item.value === this.queries.status)[0]
    } else {
      const queries = Object.assign({}, this.$route.query)
      this.queries = queries
      this.queries.page = this.queries.search_keyword ? 1 : parseInt(queries.page)
      this.keyword = this.queries.search_keyword
      this.dateRange = {
        startDate: this.queries.start_date,
        endDate: this.queries.end_date
      }
      this.queries.per_page = parseInt(queries.per_page)
      this.selectedTab = this.tabList.filter(item => item.value === this.queries.status)[0]
      if (this.queries.search_keyword && this.queries.page !== 1) {
        this.$router.push({ path: '/order/step/upload', query: this.queries })
      } else {
        this.getTempOrderList(this.queries)
      }
    }
  },
  methods: {
    ...mapActions('master', ['getCities', 'getPackageSizes', 'getGoodsCategories', 'getPickupSlotTime']),
    ...mapActions('common', ['showLoading', 'hideLoading']),
    ...mapActions('merchant', ['getAddresses', 'getDeliveryServices', 'toggleAddressModal']),
    ...mapActions('order', ['uploadOrder', 'getTempOrders', 'createMultipleOrder']),
    getTempOrderList(queries = '') {
      this.showLoading()
      this.getTempOrders({
        params: queries
      })
        .then(res => {
          this.totalRecords = res.data.meta.total_item
          this.hideLoading()
        })
        .catch(error => {
          this.hideLoading()
          console.log('error', error.response)
        })
    },
    async submitMultipleOrder() {
      this.showLoading()

      try {
        const res = await this.createMultipleOrder({ payloads: {} })
      } catch (error) {
        console.error(error)
      } finally {
        this.hideLoading()
        this.$router.push({ path: '/order' })
      }
    },
    downloadTemplate() {
      // Path to your template file inside /public/files
      const fileUrl = '/files/OrderTemplate.xlsx'

      const link = document.createElement('a')
      link.href = fileUrl
      link.setAttribute('download', 'OrderTemplate.xlsx') // force download
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    onFileChange(e) {
      const file = e.target.files[0]
      if (file) {
        this.uploadedFile = file
      }
    },
    resetFile() {
      this.uploadedFile = null
      this.$refs.fileUpload.value = null
    },
    manualInput() {
      this.isUsingExistingReceiverAddress = false
      this.receiverAddress = null
      this.receiverName = null
      this.receiverPhoneNumber = null
      this.destinationSearch = ''
      this.addressDetail = null
    },
    toggleReceiverAddressModal() {
      this.visibleAddressModal = !this.visibleAddressModal
    },
    applyReceiverAddress(value) {
      this.receiverAddress = value
      this.isUsingExistingReceiverAddress = true
      this.receiverName = value.sender_name
      this.receiverPhoneNumber = value.sender_phone
      this.destinationSearch = `${value.area.village}, ${value.area.area_display}`
      this.addressDetail = `${value.address} ${value.detail_address ? '(' + value.detail_address + ')' : ''}`
      this.toggleReceiverAddressModal()
    },
    getPickupSlotTimeList() {
      this.getPickupSlotTime()
    },
    numbersOnly(evt) {
      evt = evt ? evt : window.event
      var charCode = evt.which ? evt.which : evt.keyCode
      if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 46) {
        evt.preventDefault()
      } else {
        return true
      }
    },
    addAddress() {
      this.toggleAddressModal({
        state: !this.isAddressModalVisible,
        mode: 'RELOAD'
      })
    },
    validForm(payload) {
      const error = isNotEmptyError(payload)
      return Object.keys(error).length === 0
    },
    toggleCustomModal() {
      this.visibleCustom = !this.visibleCustom
    },
    cancelCreateOrder() {
      this.showLoading()
      localStorage.removeItem('order_shipping')
      setTimeout(() => {
        this.hideLoading()
        this.$router.push('/order')
      }, 1000)
    },
    goBack(target) {
      this.showLoading()
      setTimeout(() => {
        this.$router.push(`/order/step/${target}`)
        this.hideLoading()
      }, 1000)
    },
    // getSenderAddress() {
    //   this.getAddresses({
    //     params: {
    //       location_label: ''
    //     }
    //   }).then(() => {
    //     const data = JSON.parse(localStorage.getItem('order_shipping'))
    //     if (!data) {
    //       this.selectedAddress = this.defaultAddress
    //     } else {
    //       this.selectedAddress = data.sender
    //     }
    //   })
    // },
    selectPackageSize(size) {
      if (size.code === 'CUSTOM') {
        this.toggleCustomModal()
      } else {
        this.goodsWeight = size.max_weight
        this.selectedPackageSize = size
      }
    },
    applyCustom() {
      const customPackage = this.packageSizes.filter(item => item.code === 'CUSTOM')[0]
      customPackage.dimension = `${this.custom.length}x${this.custom.width}x${this.custom.height}`
      this.goodsWeight = this.custom.weight
      this.selectedPackageSize = customPackage
      this.toggleCustomModal()
    },
    displaySuggestions(value) {
      this.suggestionList = value
    },
    searchPlace(input) {
      if (this.searchKeyword !== '') {
        clearTimeout(this.debounce)
        let self = this
        this.debounce = setTimeout(() => {
          if (this.searchKeyword !== '') {
            this.isSearchingPlace = true
            this.autocompleteService.getPlacePredictions(
              {
                input: self.searchKeyword,
                sessionToken: self.sessionToken,
                componentRestrictions: { country: 'id' }
              },
              this.displaySuggestions
            )
          }
        }, 300)
      } else {
        this.isSearchingPlace = false
      }
    },
    selectPlace(place) {
      let request = {
        placeId: place.place_id,
        fields: ['name', 'geometry', 'formatted_address']
      }
      this.autocompleteService.get
      this.searchKeyword = place.description
      this.isSearchingPlace = false
      this.placeService.getDetails(request, this.setAddress)
    },
    setAddress(place) {
      this.address = {
        name: place.formatted_address,
        latitude: place.geometry.location.lat(),
        longitude: place.geometry.location.lng()
      }
    },
    getGoodsCategoryList() {
      this.getGoodsCategories()
    },
    getPackageSizeList() {
      this.showLoading()
      this.getPackageSizes().then(() => {
        this.hideLoading()
        const data = JSON.parse(localStorage.getItem('order_shipping'))

        if (data) {
          this.selectedPackageSize = data.package
          if (this.selectedPackageSize.code === 'CUSTOM') {
            this.custom.weight = data.receiver?.weight
            const dim = this.selectedPackageSize.dimension.split('x')
            this.custom.length = dim[0]
            this.custom.width = dim[1]
            this.custom.height = dim[2]
          }
        }
      })
    },
    searchAddress(value, target) {
      this.senderAddressKeyword = null
      if (value) this.isSearching[target] = true
      this.visibleSuggestion[target] = false
      clearTimeout(this.debounce)
      this.debounce = setTimeout(() => {
        if (value) {
          this.senderAddressKeyword = value
          this.getAddresses({
            params: {
              location_label: this.senderAddressKeyword
            }
          }).then(() => {
            this.visibleSuggestion[target] = true
            this.isSearching[target] = false
          })
        }
      }, 200)
    },
    selectAddress(data, target) {
      console.log('data', data)
      this.senderAddressSearch = data.location_label
      this.selectedAddress = data
      this.visibleSuggestion[target] = false
      this.payloads = {
        sender_name: data.sender_name,
        sender_phone: data.sender_phone,
        sender_address: data.area.area_display,
        sender_location_code: data.area.code
      }
    },
    async submitFile() {
      const file = this.$refs.fileUpload.files[0]
      if (!file) {
        alert('Pilih file dulu')
        return
      }

      this.uploadIsLoading = true
      try {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('sender_name', this.payloads.sender_name)
        formData.append('sender_phone', this.payloads.sender_phone)
        formData.append('sender_address', this.payloads.sender_address)
        formData.append('sender_location_code', this.payloads.sender_location_code)

        // ✅ wait until uploadOrder really finishes
        const res = await this.uploadOrder({ payloads: formData })
        console.log('Response:', res.data)

        await this.getTempOrderList(this.queries) // refresh list
      } catch (error) {
        console.error(error)
      } finally {
        this.uploadIsLoading = false
      }
    },
    searchCity(value, target) {
      this.cityKeyword = null
      if (value) this.isSearching[target] = true
      this.visibleSuggestion[target] = false
      clearTimeout(this.debounce)
      this.debounce = setTimeout(() => {
        if (value) {
          this.cityKeyword = value
          this.getCities({
            params: {
              page: 1,
              per_page: 1000,
              search_keyword: this.cityKeyword
            }
          }).then(() => {
            this.visibleSuggestion[target] = true
            this.isSearching[target] = false
          })
        }
      }, 200)
    },
    selectCity(data, target) {
      if (target === 'origin') {
        this.payloads.origin_city = data
        this.originSearch = `${data.village}, ${data.area_display}`
      } else {
        this.payloads.destination_city = data
        this.destinationSearch = `${data.village}, ${data.area_display}`
      }
      this.visibleSuggestion[target] = false
    }
  }
}
</script>
